import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

import {
  ACCOUNT_ADD_BUSINESS_DETAILS_LINK,
  ACCOUNT_ADD_BUSINESS_LINK,
  ACCOUNT_BRANCH_SELECTION_CHECK_LINK,
  ACCOUNT_TAX_ID_CHECK_LINK,
  ACCOUNT_VAT_NUMBER_CHECK_LINK,
  AUTH_EMAIL_VALIDATION_LINK,
  AUTH_LOGIN_LINK,
  AUTH_PASSWORDLESS_ADD_BUSINESS_DETAILS_LINK,
  AUTH_PASSWORDLESS_SIGNUP_LINK,
  AUTH_REGISTER_LINK,
  AUTH_VALIDATE_EMAIL_LINK,
} from '~shared/constants/links.constants';
import { PageService } from '~shared/services/page.service';
import { BackLinkMetadata } from '../../models/back-link-metadata.model';
import { AuthService } from '~app/modules/authorization/services/authorization-idam.service';
import { CheckEmailService } from '~app/modules/authorization/services/check.email.service';
import { CompanyDetailsFromVatService } from '~app/modules/authorization/services/company-details-from-vat.service';

// prettier-ignore
import {
  CountryBasedTierTwoConfigService
} from '~app/modules/authorization/services/country-based-tier-two-config.service';
import { APP_USER_SECURE_LOGIN_NOTICE_ROUTE, CHECKOUT_CART_ROUTE, CountryCode } from '~shared/constants';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { AuthorizationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationAuthorizationActionType } from '~shared/modules/application-event/constants';
import { FF_MAGIC_LINK_AUTH } from '../../constants/authorization.constant';
import { environment } from '~env/environment';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType } from '@metromarkets/components';
import { TranslateService } from '@ngx-translate/core';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { MediaObserver } from '@angular/flex-layout';
import { CompanyDetailsFromIdService } from '~app/modules/authorization/services/company-details-from-id.service';
import { UrlUtil } from '~shared/utils/url.util';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Component({
  selector: 'app-authorization',
  templateUrl: './authorization.component.html',
  styleUrls: ['./authorization.component.scss'],
})
export class AuthorizationComponent implements OnInit, OnDestroy {
  magicLinkIcon = `${environment.cdnBaseUrl()}/images/magic-link-mail-icon.svg`;
  isMobileOrTablet = false;

  public readonly loginPageLink = AUTH_LOGIN_LINK;
  public readonly registrationPageLink = AUTH_REGISTER_LINK;
  public secureLoginEmail: string;
  public secureLoginEmailQueryParam: string;
  public pageAlias$: Observable<string>;
  public emailExists$: Observable<boolean>;
  public backLinkMetadata: Observable<BackLinkMetadata>;
  private unsubscribe$ = new Subject<any>();
  private secureLoginBackUrl: string;
  private secureLoginRedirectUrl: string;

  // prettier-ignore
  constructor( // NOSONAR
    private router: Router,
    private pageService: PageService,
    private idamAuthService: AuthService,
    private checkEmailService: CheckEmailService,
    private companyDetailsFromVatService: CompanyDetailsFromVatService,
    private companyDetailsFromIdService: CompanyDetailsFromIdService,
    private countryBasedTierTwoConfigService: CountryBasedTierTwoConfigService,
    private applicationEventService: ApplicationEventService,
    private alertService: MmAlertService,
    private readonly translateService: TranslateService,
    private mediaObserver: MediaObserver,
    private activatedRoute: ActivatedRoute,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
  }

  @ViewChild('email_redirect_container', { static: false }) set emailRedirect(elRef: ElementRef) {
    if (elRef) {
      fromEvent(elRef.nativeElement.querySelector('a'), 'click')
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          const { queryParams } = this.router.parseUrl(this.router.url);
          const { backUrl } = queryParams;
          this.idamAuthService.login(backUrl);
        });
    }
  }

  public ngOnInit(): void {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isMobileOrTablet = this.mediaObserver.isActive(MEDIA_ALISES.mobile) || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
      });

    this.pageAlias$ = this.pageService.currentPageAlias$;
    this.backLinkMetadata = this.getBackLinkMetadata();

    this.emailExists$ = this.checkEmailService.emailExistence$;

    this.activatedRoute.queryParams.subscribe((params: Params) => {
      const isSharePaymentLinkRedirection = Boolean(params.login_id && params.redirect_uri);

      if (isSharePaymentLinkRedirection) {
        this.secureLoginEmailQueryParam = params.login_id;
        this.secureLoginRedirectUrl = decodeURIComponent(params.redirect_uri);

        UrlUtil.removeQueryParameters(['locale_id', 'password_less', 'login_id', 'redirect_uri'], this.router);

        this.sendMagicLink().subscribe();
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event: NavigationEnd) => {
        this.backLinkMetadata = this.getBackLinkMetadata();
        this.secureLoginEmail = this.router.getCurrentNavigation().extras.state?.email;
        this.secureLoginBackUrl = this.router.getCurrentNavigation().extras.state?.backUrl;
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public idamLogin(): void {
    const { queryParams } = this.router.parseUrl(this.router.url);
    this.idamAuthService.login(queryParams.backUrl);
  }

  getBackLinkMetadata(): Observable<BackLinkMetadata> {
    const RETURN_TO_PREVIOUS_PAGE_TITLE = 'REGULAR.RETURN_TO_PREVIOUS_PAGE';
    const baseMetaData = {
      isVisible: true,
      title: RETURN_TO_PREVIOUS_PAGE_TITLE,
    };

    return combineLatest([
      this.countryBasedTierTwoConfigService.getCompanyDetailsFromVATId$,
      this.countryBasedTierTwoConfigService.getCompanyDetailsFromTaxId$,
    ]).pipe(
      // prettier-ignore
      map(([isFFCompanyDataFromVATFFEnabled, isFFCompanyDataFromKVKFFEnabled]) => { // NOSONAR
        const market = getCountryDomainCode(this.webAppBuyerRequest);
        const isItalianMarket = market === CountryCode.Italy;
        const isDutchMarket = market === CountryCode.Netherlands;
        const isFrenchMarket = market === CountryCode.France;
        const isPortugalMarket = market === CountryCode.Portugal;
        if (this.pageService.pathStartsWith(ACCOUNT_VAT_NUMBER_CHECK_LINK)) {
          return { ...baseMetaData, link: ACCOUNT_ADD_BUSINESS_LINK };
        }

        if (this.pageService.pathStartsWith(ACCOUNT_TAX_ID_CHECK_LINK)) {
          this.companyDetailsFromIdService.clearSelectedBranchDataForTierTwo();
          return { ...baseMetaData, link: ACCOUNT_ADD_BUSINESS_LINK };
        }

        if (this.pageService.pathStartsWith(ACCOUNT_BRANCH_SELECTION_CHECK_LINK)) {
          this.companyDetailsFromIdService.clearSelectedBranchDataForTierTwo();
          return { ...baseMetaData, link: ACCOUNT_TAX_ID_CHECK_LINK };
        }

        if (this.pageService.pathStartsWith(AUTH_PASSWORDLESS_SIGNUP_LINK)) {
          return { ...baseMetaData, link: AUTH_VALIDATE_EMAIL_LINK };
        }

        if (this.pageService.pathStartsWith(ACCOUNT_ADD_BUSINESS_DETAILS_LINK)) {
          if (
            isFFCompanyDataFromVATFFEnabled &&
            (isPortugalMarket || isItalianMarket)
          ) {
            this.companyDetailsFromVatService.clearCompanyData();

            return { ...baseMetaData, link: ACCOUNT_VAT_NUMBER_CHECK_LINK };
          }

          if (
            isFFCompanyDataFromKVKFFEnabled &&
            (isDutchMarket || isFrenchMarket)
          ) {
            this.companyDetailsFromIdService.clearSelectedBranchDataForTierTwo();
            this.companyDetailsFromIdService.clearCompanyData();
            return { ...baseMetaData, link: ACCOUNT_TAX_ID_CHECK_LINK };
          }

          return {
            ...baseMetaData,
            link: ACCOUNT_ADD_BUSINESS_LINK,
          };
        }

        if (this.pageService.pathStartsWith(AUTH_PASSWORDLESS_ADD_BUSINESS_DETAILS_LINK)) {
          return {
            isVisible: true,
            link: AUTH_PASSWORDLESS_SIGNUP_LINK,
            title: RETURN_TO_PREVIOUS_PAGE_TITLE,
          };
        }

        if (this.pageService.pathStartsWith(AUTH_REGISTER_LINK)) {
          return {
            ...baseMetaData,
            link: this.registrationPageLink,
          };
        }
        if (this.pageService.pathStartsWith(AUTH_EMAIL_VALIDATION_LINK)) {
          return {
            isVisible: true,
            link: this.loginPageLink,
            title: 'PAGES.AUTH.ELEMENTS.RETURN_TO_LOGIN',
          };
        }

        if (this.pageService.pathEndsWith(APP_USER_SECURE_LOGIN_NOTICE_ROUTE)) {
          return {
            ...baseMetaData,
            link: AUTH_VALIDATE_EMAIL_LINK,
            title: RETURN_TO_PREVIOUS_PAGE_TITLE,
          };
        }

        return { isVisible: false };
      })
    );
  }

  public get passwordlessLoginEmail() {
    return this.secureLoginEmail ?? this.secureLoginEmailQueryParam;
  }

  public resendMagicLink() {
    const title$ = this.translateService.get('PAGES.AUTH.SECURE_LOGIN_NOTICE.EMAIL_SENT');
    const message$ = this.translateService.get('PAGES.AUTH.SECURE_LOGIN_NOTICE.EMAIL_SENT_CONTENT');

    this.applicationEventService.publishIfFFEnabled(
      FF_MAGIC_LINK_AUTH,
      new AuthorizationApplicationEvent(ApplicationAuthorizationActionType.ResendLoginLinkClicked)
    );

    combineLatest([title$, message$, this.sendMagicLink()]).subscribe(([title, message]) => {
      this.alertService.addAlert({
        title,
        message,
        type: MmAlertType.success,
        mode: MmAlertMode.alert,
        size: MmAlertSize.large,
        contentAlign: 'center',
      });
    });
  }

  private sendMagicLink() {
    return this.idamAuthService.sendMagicLink({
      email: this.passwordlessLoginEmail,
      backUrl: this.secureLoginBackUrl,
      redirectionUrl: this.secureLoginRedirectUrl,
      expirationRoute: CHECKOUT_CART_ROUTE,
    });
  }
}
